import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Search, BookOpen, MessageCircle, Printer } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const PartLabel = ({ part, toc }) => (toc
  ? <div className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{part}</div>
  : <div className="mb-6 mt-2 rounded-xl bg-[#00247D] text-white px-5 py-3 print:bg-white print:text-[#00247D] print:border-2 print:border-[#00247D]" data-testid={`manual-part-${part.split(' ')[1]}`}>
    <span className="font-black tracking-wide uppercase text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{part}</span>
  </div>);

const withParts = (list, render, toc) => list.map((s, i) => (
  <React.Fragment key={s.id}>
    {s.part && s.part !== list[i - 1]?.part && <PartLabel part={s.part} toc={toc} />}
    {render(s)}
  </React.Fragment>
));

// Shared manual page: sticky searchable TOC, optional "Part" groups, print / save as PDF.
export const ManualLayout = ({ sections, title, subtitle, updated, badge = 'USER MANUAL', seo, back = { to: '/my-schedules', label: 'Back to dashboard' }, children }) => {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(sections[0]?.id);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.target.offsetTop - b.target.offsetTop)[0];
      if (visible) setActiveId(visible.target.id);
    }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });
    sections.forEach(s => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    const hash = window.location.hash.slice(1);
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView(), 60);
    return () => observer.disconnect();
  }, [sections]);
  const q = query.trim().toLowerCase();
  const filtered = q ? sections.filter(s => `${s.title} ${s.part || ''} ${s.searchText}`.toLowerCase().includes(q)) : sections;
  const printTimer = useRef(null);
  const print = () => {
    setQuery('');
    clearTimeout(printTimer.current);
    printTimer.current = setTimeout(() => window.print(), 150);
  };
  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      <SEOHead {...seo} />
      <div className="max-w-6xl mx-auto px-4 py-8 print:py-0">
        <div className="flex items-center justify-between gap-2 mb-6 print:hidden">
          <Link to={back.to}><Button variant="ghost" size="sm" data-testid="manual-back-btn"><ArrowLeft className="w-4 h-4 mr-2" />{back.label}</Button></Link>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={print} data-testid="manual-print-btn"><Printer className="w-4 h-4 mr-1.5" />Print / Save as PDF</Button>
            <div className="hidden sm:flex items-center gap-2 text-[#00247D]">
              <BookOpen className="w-5 h-5" /><span className="font-bold tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>{badge}</span>
            </div>
          </div>
        </div>
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-3" data-testid="manual-title">{title}</h1>
          <p className="text-slate-600 text-lg">{subtitle}</p>
          <p className="text-slate-500 text-sm mt-1">Last updated: {updated}</p>
          {children}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
          <aside className="lg:sticky lg:top-6 lg:self-start lg:max-h-[85vh] lg:overflow-y-auto print:hidden">
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search manual…" data-testid="manual-search"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#00247D]" />
            </div>
            <nav className="space-y-1 text-sm">
              {withParts(filtered, s => (
                <a href={`#${s.id}`} data-testid={`manual-toc-${s.id}`}
                  className={`block px-3 py-2 rounded-md transition-colors ${activeId === s.id ? 'bg-[#00247D] text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}>{s.title}</a>
              ), true)}
              {!filtered.length && <p className="text-slate-400 text-xs px-3 py-2" data-testid="manual-no-results">No sections match "{query}".</p>}
            </nav>
            <div className="mt-6 p-3 bg-[#00247D]/5 border border-[#00247D]/20 rounded-lg text-xs text-slate-700">
              <p className="font-semibold text-[#00247D] mb-1 flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> Need a specific answer?</p>
              <p>Click the chat bubble in the bottom-right to ask the assistant any platform question.</p>
            </div>
          </aside>
          <main className="prose prose-slate max-w-none pb-24 min-w-0">
            {withParts(filtered, s => (
              <section id={s.id} className="mb-10 scroll-mt-6 break-inside-avoid-page" data-testid={`manual-section-${s.id}`}>
                <h2 className="text-2xl font-black text-slate-900 mb-3 border-b border-slate-200 pb-2">{s.title}</h2>
                <div className="space-y-3 text-slate-700 leading-relaxed">{s.body}</div>
              </section>
            ), false)}
          </main>
        </div>
      </div>
    </div>
  );
};
